/*#
# float

A floating, potentially draggable user interface element.

```html
<tosi-float class="float" remain-on-resize="remain" remain-on-scroll="remain" drag>
  <h4>Drag Me</h4>
  <div class="no-drag balloon">🎈</div>
  <div class="behavior">I ignore resizing and scrolling</div>
  <footer style="font-size: 75%">neunundneunzig pixel-ballon</footer>
</tosi-float>

<tosi-float class="float" remain-on-scroll="remain" style="top: 50px; right: 20px;" drag>
  <h4>Drag Me</h4>
  <div class="no-drag balloon">🎈</div>
  <div class="behavior">I disappear on resize</div>
  <footer style="font-size: 75%">neunundneunzig pixel-ballon</footer>
</tosi-float>

<tosi-float class="float" remain-on-resize="remain" remain-on-scroll="remove" style="bottom: 20px; left: 50px;" drag>
  <h4>Drag Me</h4>
  <div class="no-drag balloon">🎈</div>
  <div class="behavior">I disappear on scroll</div>
  <footer style="font-size: 75%">neunundneunzig pixel-ballon</footer>
</tosi-float>
```
```css
.preview .float {
  width: 220px;
  height: 180px;
  padding: 0;
  gap: 5px;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  background: #fff8;
  box-shadow: 2px 10px 20px #0004;
  overflow: hidden;
  cursor: move;
}

.preview h4 {
  margin: 0;
  padding: 5px 10px;
  color: white;
  background: #c00;
}

.preview .balloon {
  cursor: default;
  flex: 1 1 auto;
  font-size: 99px;
  line-height: 120px;
  text-align: center;
  height: auto;
  overflow: hidden;
}

.preview .behavior {
  position: absolute;
  bottom: 16px;
  left: 8px;
  right: 8px;
  background: #fffc;
}

.preview footer {
  text-align: center;
  background: #b00c;
  color: white;
```

## Styling

Note that the `<tosi-float>` element has absolutely minimal styling. It's up to you to provide a drop
shadow and background and so on.

## Attributes

- `drag` false | true — to make a `<tosi-float>` element draggable, simply set its `drag` attribute.
- `remain-on-resize` 'remove' | 'hide' | 'remain' — by default, floats will hide if the window is resized
- `remain-on-scroll` 'remain' | 'remove' | 'hide' — by default, floats will remain if the document is scrolled

A float made by `popFloat` (menus, tooltips) has an **anchor**, the element it was popped from, and only a
scroll that MOVES the anchor (the page, or a scroller containing it) hides or removes it. Before 1.16.3 any
scroll anywhere did, so filtering a table on every keystroke (which scrolls it) closed a menu or hint list the
moment it opened. A float with no anchor still reacts to any scroll outside itself.

To prevent dragging for an interior element (e.g. if you want a floating palette with buttons or input fields)
just add the `no-drag` class to an element or its container.
*/

/*{ "headTitle": "<tosi-float> draggable floating panel — tosijs-ui", "parent": "Components", "description": "<tosi-float> is a minimally styled floating element that can be dragged, and that hides, stays or is removed when the window resizes." }*/

import { elements, ElementCreator, withAttributes } from 'tosijs'
import { trackDrag, bringToFront } from './track-drag.js'
import type { FloatPosition } from './pop-float.js'

const { slot } = elements

export class TosiFloat extends withAttributes({
  drag: false,
  remainOnResize: 'remove' as 'hide' | 'remove' | 'remain',
  remainOnScroll: 'remain' as 'hide' | 'remove' | 'remain',
}) {
  static preferredTagName = 'tosi-float'
  static floats: Set<TosiFloat> = new Set()

  /**
   * The element this float was popped from (set by `popFloat`). Only a scroll that moves it
   * (the page, or a scroller containing it) triggers `remainOnScroll`. `null`: any scroll does.
   */
  anchor: Element | null = null

  /**
   * The position it was asked for against its anchor (set by `positionFloat`), so it can be
   * re-fitted when the visible viewport changes. `null`: never positioned against an anchor.
   */
  // typed FloatPosition, not string: as `string` it accepted the CSS position '' / 'fixed'
  // that a shadowed variable stored there (1.16.5 review B1), and the compiler said nothing
  anchorPosition: FloatPosition | null = null

  content = slot()

  static shadowStyleSpec = {
    ':host': {
      position: 'fixed',
    },
  }

  reposition = (event: Event) => {
    if (event instanceof MouseEvent && event.button !== 0) {
      return
    }
    const target = event.target as HTMLElement | null
    if (target?.closest('.no-drag')) {
      return
    }
    if (this.drag) {
      bringToFront(this)
      const x = this.offsetLeft
      const y = this.offsetTop

      trackDrag(
        event as PointerEvent,
        (
          dx: number,
          dy: number,
          pointerEvent: PointerEvent
        ): true | undefined => {
          this.style.left = `${x + dx}px`
          this.style.top = `${y + dy}px`
          this.style.right = 'auto'
          this.style.bottom = 'auto'
          if (pointerEvent.type === 'mouseup') {
            return true
          }
        }
      )
    }
  }

  connectedCallback(): void {
    super.connectedCallback()

    ensureFloatListeners()
    TosiFloat.floats.add(this)
    const PASSIVE = { passive: true }
    this.addEventListener('touchstart', this.reposition, PASSIVE)
    /*
    `mousedown` is deliberately NOT passive — `trackDrag` calls `preventDefault()` on it, and a
    passive listener makes that a silent no-op. See track-drag.ts for what Firefox does without
    it. `passive` costs nothing to drop here: its practical value is silencing the console
    warning about handlers that delay scrolling, and that warning only ever applied to touch and
    wheel. `touchstart` stays passive for exactly that reason.
    */
    this.addEventListener('mousedown', this.reposition)
    bringToFront(this)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()

    TosiFloat.floats.delete(this)
  }
}

/** @deprecated Use TosiFloat instead */
export type XinFloat = TosiFloat
/** @deprecated Use TosiFloat instead */
export const XinFloat: typeof TosiFloat = TosiFloat

export const tosiFloat = TosiFloat.elementCreator() as ElementCreator<TosiFloat>

/** @deprecated Use tosiFloat instead */
export const xinFloat = tosiFloat

/*
Does this scroll move the anchor? The page scrolling does; so does any scroller that contains it,
across shadow roots (a menu popped from inside a component's shadow DOM is still moved by a
scroller around the component). Any other scroller — a table re-filtering, a sidebar — doesn't.
*/
function scrollMovesAnchor(
  scrolled: EventTarget | null,
  anchor: Element | null
): boolean {
  if (anchor === null) return true
  if (
    scrolled === document ||
    scrolled === document.documentElement ||
    scrolled === document.body
  )
    return true
  if (!(scrolled instanceof Node)) return true
  const visited = new Set<Node>() // floats anchored inside each other must not loop
  for (let node: Node | null = anchor; node && !visited.has(node); ) {
    visited.add(node)
    if (node === scrolled) return true
    /*
    A float's anchor can be INSIDE another float — a submenu's anchor is an item of its parent
    menu, and the parent float is mounted on <body>, so walking up from it never meets the
    scroller around the root menu button. Continue from the containing float's own anchor:
    a chain of menus moves (and closes) together. Before this, scrolling that scroller closed
    the parent and left the submenu on screen, orphaned (1.16.3 review B1).
    */
    if (node instanceof TosiFloat && node.anchor) {
      node = node.anchor
      continue
    }
    node = node.parentNode ?? (node as ShadowRoot).host ?? null
  }
  return false
}

// Register the global reposition/dismiss handlers on first float connecting,
// not at import (keeps the module side-effect-free for tree-shaking). They
// iterate TosiFloat.floats, so they're no-ops until a float exists — registered
// once, no teardown needed.
let floatListenersRegistered = false
function ensureFloatListeners(): void {
  if (floatListenersRegistered) return
  floatListenersRegistered = true
  window.addEventListener(
    'resize',
    () => {
      Array.from(TosiFloat.floats).forEach((float: TosiFloat) => {
        if (float.remainOnResize === 'hide') {
          float.hidden = true
        } else if (float.remainOnResize === 'remove') {
          float.remove()
        }
      })
    },
    { passive: true }
  )

  document.addEventListener(
    'scroll',
    (event: Event) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest(TosiFloat.tagName as string)
      ) {
        return
      }
      Array.from(TosiFloat.floats).forEach((float: TosiFloat) => {
        if (!scrollMovesAnchor(event.target, float.anchor)) return
        if (float.remainOnScroll === 'hide') {
          float.hidden = true
        } else if (float.remainOnScroll === 'remove') {
          float.remove()
        }
      })
    },
    { passive: true, capture: true }
  )
}
